import React, { useEffect, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { reverse } from "named-urls";
import { Table, Space, PageHeader } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useInjectReducer, useInjectSaga } from "redux-injectors";
import { sliceKey, reducer, actions } from "./slice";
import { companyCatalogListingSaga } from "./saga";
import { useAuth } from "../../../../contexts/auth";
import * as selectors from "./selectors";
import routes from "../../../../../routes";

const STORAGE_KEY = 'companyCatalogListingFilters';

const CompanyCatalogListing = () => {
  const auth = useAuth();
  const user = auth.loggedInUser;
  const userRole = user.role;

  useInjectReducer({ key: sliceKey, reducer: reducer });
  useInjectSaga({ key: sliceKey, saga: companyCatalogListingSaga });
  const dispatch = useDispatch();

  const history = useHistory();
  const location = useLocation();

  // Initialize state from sessionStorage
  const getInitialState = () => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.error('Failed to parse saved filters:', e);
    }
    return { page: 1, companyName: null };
  };

  const initialState = getInitialState();
  const [currentPage, setCurrentPage] = useState(initialState.page);
  const [companyFilter, setCompanyFilter] = useState(initialState.companyName);

  const companies = useSelector(selectors.selectSummaryData) || [];
  const loading = useSelector(selectors.selectLoading);
  const totalCompanyCount = useSelector(selectors.selectCompanyCount);

  // Save state to sessionStorage whenever it changes
  useEffect(() => {
    const state = { page: currentPage, companyName: companyFilter };
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
      console.error('Failed to save filters:', e);
    }
  }, [currentPage, companyFilter]);

  // Fetch data when filters change
  useEffect(() => {
    let query = `page=${currentPage}&limit=10`;
    if (companyFilter) query += `&companyName=${companyFilter}`;
    dispatch(actions.fetchCompanyCatalogSummary({ query }));
  }, [dispatch, currentPage, companyFilter]);

  const companyData = companies.map((company) => ({
    id: company._id,
    key: company._id,
    name: company.companyName,
    count: company.catalog?.length,
    catalogAvailable: company.catalogAvailable,
  }));

  // Sorted filter list
  const getCompanyFilter = () => {
    if (!companies?.length) return [];
    return companies
      .map((company) => ({
        text: company.companyName,
        value: company.companyName,
      }))
      .sort((a, b) => a.text.localeCompare(b.text));
  };

  // Handle pagination and filters
  const handleTablePagination = (pagination, filters, sorter) => {
    setCurrentPage(pagination.current || 1);
    setCompanyFilter(filters.name?.[0] || null);
  };
  const columns = [
    {
      title: "Company Name",
      dataIndex: "name",
      key: "name",
      filters: getCompanyFilter(),
      filterMode: "menu",
      filterSearch: true,
      filteredValue: companyFilter ? [companyFilter] : null,
    },
    {
      title: "Actions",
      key: "actions",
      render: (value, record) => (
        <Space size="middle">
          {record.catalogAvailable ? (
            <>
              <Link
                to={reverse(routes.backOffice.companies.details.catalog.list, {
                  companyId: record.id,
                })}
              >
                View
              </Link>
              {userRole !== "support_team" && (
                <Link
                  to={reverse(routes.backOffice.companies.details.catalog.edit, {
                    companyId: record.id,
                  })}
                >
                  Edit
                </Link>
              )}
            </>
          ) : (
            userRole !== "support_team" && (
              <Link
                to={reverse(routes.backOffice.companies.details.catalog.add, {
                  companyId: record.id,
                })}
              >
                Create
              </Link>
            )
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ height: "100%" }}>
      <PageHeader ghost={false} title="Company Catalogs" />
      <div
        style={{
          height: "calc(100vh - 137px)",
          overflowY: "auto",
          padding: 24,
        }}
      >
        <Table
          dataSource={companyData}
          columns={columns}
          loading={loading}
          pagination={{
            current: currentPage,
            pageSize: 10,
            total: totalCompanyCount,
            showSizeChanger: false,
            disabled: companyData.length === 0,
          }}
          onChange={handleTablePagination}
        />
      </div>
    </div>
  );
};

export default CompanyCatalogListing;